// Particle Animator - Animates particles along streamlines
// Uses requestAnimationFrame for smooth 60fps animation

class ParticleAnimator {
  constructor(renderingEngine) {
    this.engine = renderingEngine;
    this.particles = [];
    this.particleSystem = null;
    this.streamlines = [];
    this.isAnimating = false;
    this.animationId = null;
    this.speed = 1.0;
    this.particleSize = 0.005;
    this.particleCount = 100;
    this.currentColormap = 'jet';
    this.scalarRange = { min: 0, max: 1 };
    this.lastTime = 0;
  }

  /**
   * Initialize particles from streamline data
   * @param {Object} data - API response containing streamlines data
   * @param {Object} options - Animation options (speed, size, count, colormap)
   */
  initialize(data, options = {}) {
    console.log('Initializing particle animator...', data);

    if (!data || !data.streamlines || data.streamlines.length === 0) {
      console.warn('No streamline data for particle animation');
      return;
    }

    // Clear existing particles
    this.clear();

    // Extract options
    this.speed = options.speed || 1.0;
    this.particleSize = options.particleSize || 0.005;
    this.particleCount = options.particleCount || 100;
    this.currentColormap = options.colormap || 'jet';

    // Store streamlines
    this.streamlines = data.streamlines;

    // Calculate scalar range for coloring
    this.calculateScalarRange(this.streamlines);

    // Create particles
    this.createParticles();

    console.log(`Created ${this.particles.length} particles across ${this.streamlines.length} streamlines`);
    return true;
  }

  /**
   * Create particle instances distributed across streamlines
   */
  createParticles() {
    const particlesPerStreamline = Math.max(1, Math.floor(this.particleCount / this.streamlines.length));

    this.streamlines.forEach((streamline, streamlineIdx) => {
      if (!streamline.points || streamline.points.length < 2) return;

      // Create multiple particles for this streamline
      for (let i = 0; i < particlesPerStreamline; i++) {
        // Distribute particles evenly along the streamline's progress
        const progress = i / particlesPerStreamline;

        this.particles.push({
          streamlineIdx: streamlineIdx,
          progress: progress,
          position: new THREE.Vector3(),
          velocity: new THREE.Vector3(),
          color: new THREE.Color(),
          speed: 0
        });
      }
    });

    // Create THREE.js particle system
    this.createParticleSystem();
  }

  /**
   * Create THREE.js Points geometry for particles
   */
  createParticleSystem() {
    const geometry = new THREE.BufferGeometry();
    const positions = new Float32Array(this.particles.length * 3);
    const colors = new Float32Array(this.particles.length * 3);
    const sizes = new Float32Array(this.particles.length);

    // Initialize particle positions and colors
    this.particles.forEach((particle, i) => {
      this.updateParticlePosition(particle);

      positions[i * 3] = particle.position.x;
      positions[i * 3 + 1] = particle.position.y;
      positions[i * 3 + 2] = particle.position.z;

      colors[i * 3] = particle.color.r;
      colors[i * 3 + 1] = particle.color.g;
      colors[i * 3 + 2] = particle.color.b;

      sizes[i] = this.particleSize;
    });

    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
    geometry.setAttribute('size', new THREE.BufferAttribute(sizes, 1));

    // Create shader material for smooth particles
    const material = new THREE.ShaderMaterial({
      uniforms: {
        pointSize: { value: this.particleSize * 1000 }
      },
      vertexShader: `
        attribute float size;
        attribute vec3 color;
        varying vec3 vColor;

        void main() {
          vColor = color;
          vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
          gl_PointSize = size * (300.0 / -mvPosition.z);
          gl_Position = projectionMatrix * mvPosition;
        }
      `,
      fragmentShader: `
        varying vec3 vColor;

        void main() {
          // Create circular particles
          vec2 coord = gl_PointCoord - vec2(0.5);
          float dist = length(coord);
          if (dist > 0.5) discard;

          // Smooth edge
          float alpha = 1.0 - smoothstep(0.3, 0.5, dist);

          gl_FragColor = vec4(vColor, alpha);
        }
      `,
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending
    });

    this.particleSystem = new THREE.Points(geometry, material);
    this.particleSystem.userData.type = 'particleStreamlines';
    this.engine.scene.add(this.particleSystem);
  }

  /**
   * Update particle position based on progress along streamline
   */
  updateParticlePosition(particle) {
    const streamline = this.streamlines[particle.streamlineIdx];
    if (!streamline || !streamline.points || streamline.points.length < 2) return;

    const points = streamline.points;
    const speeds = streamline.speed || [];

    // Wrap progress to [0, 1]
    particle.progress = particle.progress % 1.0;

    // Find position along streamline
    const index = particle.progress * (points.length - 1);
    const idx = Math.floor(index);
    const nextIdx = Math.min(idx + 1, points.length - 1);
    const t = index - idx;

    // Interpolate position
    const p1 = points[idx];
    const p2 = points[nextIdx];
    particle.position.set(
      p1[0] + t * (p2[0] - p1[0]),
      p1[1] + t * (p2[1] - p1[1]),
      p1[2] + t * (p2[2] - p1[2])
    );

    // Interpolate speed for coloring
    if (speeds.length > 0) {
      const s1 = speeds[Math.min(idx, speeds.length - 1)] || 0;
      const s2 = speeds[Math.min(nextIdx, speeds.length - 1)] || 0;
      particle.speed = s1 + t * (s2 - s1);
    }

    // Update color based on speed using centralized Colormap
    const normalizedSpeed = (particle.speed - this.scalarRange.min) /
                            (this.scalarRange.max - this.scalarRange.min + 1e-10);

    if (window.Colormap && window.Colormap.getColor) {
      // Get solid color for streamline colormap dropdown if using solid colormap
      const solidColorHex = window.Colormap.isSolidColor(this.currentColormap)
        ? window.Colormap.getSolidColor('streamlineColormap')
        : null;
      const rgb = window.Colormap.getColor(normalizedSpeed, this.currentColormap, solidColorHex);
      particle.color.setRGB(rgb[0] / 255, rgb[1] / 255, rgb[2] / 255);
    } else {
      particle.color.setRGB(0.2, 0.6, 1.0);
    }
  }

  /**
   * Start animation loop
   */
  start() {
    if (this.isAnimating) return;

    console.log('Starting particle animation...');
    this.isAnimating = true;
    this.lastTime = performance.now();
    this.animate();
  }

  /**
   * Stop animation loop
   */
  stop() {
    console.log('Stopping particle animation...');
    this.isAnimating = false;

    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
      this.animationId = null;
    }
  }

  /**
   * Animation loop using requestAnimationFrame
   */
  animate() {
    if (!this.isAnimating) return;

    const currentTime = performance.now();
    const deltaTime = (currentTime - this.lastTime) / 1000; // Convert to seconds
    this.lastTime = currentTime;

    // Update particle positions
    this.updateParticles(deltaTime);

    // Update geometry
    if (this.particleSystem) {
      const positions = this.particleSystem.geometry.attributes.position.array;
      const colors = this.particleSystem.geometry.attributes.color.array;

      this.particles.forEach((particle, i) => {
        positions[i * 3] = particle.position.x;
        positions[i * 3 + 1] = particle.position.y;
        positions[i * 3 + 2] = particle.position.z;

        colors[i * 3] = particle.color.r;
        colors[i * 3 + 1] = particle.color.g;
        colors[i * 3 + 2] = particle.color.b;
      });

      this.particleSystem.geometry.attributes.position.needsUpdate = true;
      this.particleSystem.geometry.attributes.color.needsUpdate = true;
    }

    // Continue animation
    this.animationId = requestAnimationFrame(() => this.animate());
  }

  /**
   * Update all particles based on time delta
   */
  updateParticles(deltaTime) {
    const progressIncrement = this.speed * deltaTime * 0.1; // Adjust multiplier for desired speed

    this.particles.forEach(particle => {
      // Advance particle along streamline
      particle.progress += progressIncrement;

      // Update position and color
      this.updateParticlePosition(particle);
    });
  }

  /**
   * Calculate scalar range for coloring
   */
  calculateScalarRange(streamlines) {
    let min = Infinity;
    let max = -Infinity;

    streamlines.forEach(streamline => {
      const speeds = streamline.speed || [];
      speeds.forEach(value => {
        if (value < min) min = value;
        if (value > max) max = value;
      });
    });

    this.scalarRange = {
      min: isFinite(min) ? min : 0,
      max: isFinite(max) ? max : 1
    };

    console.log('Speed range:', this.scalarRange);
  }

  /**
   * Update animation speed
   */
  setSpeed(speed) {
    this.speed = Math.max(0, speed);
  }

  /**
   * Update particle size
   */
  setParticleSize(size) {
    this.particleSize = Math.max(0.001, size);

    if (this.particleSystem) {
      const sizes = this.particleSystem.geometry.attributes.size.array;
      sizes.fill(this.particleSize);
      this.particleSystem.geometry.attributes.size.needsUpdate = true;

      // Update shader uniform
      this.particleSystem.material.uniforms.pointSize.value = this.particleSize * 1000;
    }
  }

  /**
   * Update colormap
   */
  setColormap(colormapName) {
    this.currentColormap = colormapName;

    // Update all particle colors
    this.particles.forEach(particle => {
      this.updateParticlePosition(particle);
    });
  }

  /**
   * Clear all particles
   */
  clear() {
    this.stop();

    if (this.particleSystem) {
      if (this.particleSystem.geometry) this.particleSystem.geometry.dispose();
      if (this.particleSystem.material) this.particleSystem.material.dispose();
      this.engine.scene.remove(this.particleSystem);
      this.particleSystem = null;
    }

    this.particles = [];
    this.streamlines = [];
  }

  /**
   * Show/hide particles
   */
  setVisible(visible) {
    if (this.particleSystem) {
      this.particleSystem.visible = visible;
    }
  }

  /**
   * Check if animation is running
   */
  isRunning() {
    return this.isAnimating;
  }

  /**
   * Toggle animation on/off
   */
  toggle() {
    if (this.isAnimating) {
      this.stop();
    } else {
      this.start();
    }
  }
}

// Export for use in other modules
if (typeof window !== 'undefined') {
  window.ParticleAnimator = ParticleAnimator;
}
